import { useState, useEffect, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import * as Crypto from 'expo-crypto';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Plus,
  Building2,
  ArrowRight,
  ChevronLeft,
  Check,
  Lock,
  AlertTriangle,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import type { StartupProfile } from '@/lib/state/profile-store';
import { OnboardingLayout, STARTUP_ONBOARDING_DISPLAY_STEPS } from '@/components/OnboardingLayout';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import {
  claimCompany,
  joinCompany,
  lookupSwissCompany,
  enrichFromStartupticker,
} from '@/lib/supabase/edge-functions';
// NOTE: `joinCompany` stays imported — it also backs the create-path's
// asCreator active-enroll (see handleCreate below), which is NOT the retired
// self-service "Join" plain endpoint being removed here.
import { ClaimVerificationPanel } from '@/components/ClaimVerificationPanel';
import { searchCompanies, type CompanyMatch } from '@/lib/onboarding/company-search';
import { handleError, reportError } from '@/lib/errors';
import { CLAIM_REFUSAL_MESSAGES, isClaimRefusalCode } from '@/lib/company/claim-refusals';
import { useAlert } from '@/components/AlertDialog';
import { AppModal, ModalAction } from '@/components/modal';
import { capitalizeWords } from '@/lib/format';
import { track, useTrackScreen } from '@/lib/analytics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { OnboardingSignOut } from '@/components/OnboardingSignOut';
import { canCreateOrClaim, type FounderStatus } from '@/lib/onboarding/founder-status';
import { nextStep } from '@/lib/onboarding/steps';
import { persistOnboardingStep } from '@/lib/onboarding/persist-step';
import { useEntrance } from '@/lib/a11y/reduced-motion';
import { routeForStep } from '@/lib/onboarding/resolve-route';

type MatrixAction = 'claim' | 'create';

/** Row shape returned from a `startup_profiles` insert/select — enough to
 * hydrate the Zustand store before handing off to data-input-choice. */
interface StartupProfileRow {
  id: string;
  name: string;
  tagline?: string | null;
  industry?: string | null;
  stage?: string | null;
  location?: string | null;
  founded_year?: number | null;
  team_size?: number | null;
  website?: string | null;
}

/** Per-match action per the role × page-state matrix (spec §2.2). Registry
 * hits have no backing `startup_profiles` row yet, so they're "doesn't
 * exist" from Inverio's perspective regardless of role. Self-service "Join"
 * is retired (final-review IMPORTANT 2) — a match that's already claimed, or
 * unclaimed but the caller isn't a founder/co-founder, has no tappable
 * action; the row shows invite guidance instead (see CompanyRow). */
function actionFor(match: CompanyMatch, isFounder: boolean): MatrixAction | null {
  if (match.source === 'registry') return 'create';
  if (!match.is_claimed && isFounder) return 'claim';
  return null;
}

function rowToStoreProfile(row: StartupProfileRow): StartupProfile {
  // No silent defaults (Task 3): an unset stage/funding/founded-year/team-size
  // must read as UNKNOWN, never a fabricated 'pre-seed'/$0/current-year/team-of-1
  // (those polluted scoring inputs). Mirrors mapStartupProfileRow's unset
  // handling; the cast bypasses StartupProfile's non-null field types the same
  // way the load-mapper does.
  return {
    id: row.id,
    name: row.name,
    tagline: row.tagline || '',
    about: (row as { about?: string }).about || '',
    industry: row.industry || '',
    stage: ((row.stage as StartupProfile['stage']) || null) as StartupProfile['stage'],
    location: row.location || '',
    foundedYear: row.founded_year ?? undefined,
    teamSize: row.team_size ?? undefined,
    website: row.website || undefined,
    profileSource: 'manual',
  } as unknown as StartupProfile;
}

/** No closure over component state — only the module-level `supabase`
 * client and `isSupabaseConfigured()`. Hoisted so it's not recreated every
 * render. */
async function fetchFullRow(startupId: string, fallbackName: string): Promise<StartupProfileRow> {
  if (isSupabaseConfigured()) {
    const { data } = await supabase.from('startup_profiles').select('*').eq('id', startupId).maybeSingle();
    if (data) return data as unknown as StartupProfileRow;
  }
  return { id: startupId, name: fallbackName };
}

// ------------------------------------------------------------------
// Ownership bridge — assigns team_members owner via the repurposed
// onboarding-claim function. For a company_directory-linked match this
// MUST be called only after the existing verification flow (below)
// succeeds; for an unlinked match (no directory row — created purely in
// Inverio) it can be called directly, since onboarding-claim's own
// "unlinked_create" branch requires no prior verification either way.
// Returns whether the bridge succeeded (and the ALREADY_OWNED code, if
// any, so callers can decide how to surface it).
//
// No closure over component state — only the module-level `claimCompany`.
// Hoisted so it's not recreated every render.
//
// NOTE: this bridge is shared by BOTH pure claims AND the founder create+claim
// path, so it deliberately does NOT emit the `company_resolved` activation
// event — that is fired ONCE at each resolution site (claim vs create) so a
// create+claim doesn't double-count. See handleClaim / handleVerifiedClaim
// Complete (claim) and handleCreate (create).
// ------------------------------------------------------------------
async function bridgeClaim(
  startupId: string,
): Promise<{ ok: boolean; code?: string }> {
  const { data, error, code } = await claimCompany({ startupId });
  if (error || !data) {
    return { ok: false, code };
  }
  return { ok: true };
}

export default function StartupCompanyScreen() {
  useTrackScreen('StartupCompany');
  const router = useRouter();
  const colors = useSystemThemeColors();
  const entrance = useEntrance();
  const { isDesktop } = useResponsive();
  const user = useProfileStore((s) => s.user);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const { profile: authProfile, user: authUser } = useAuth();
  const { error: showError } = useAlert();
  const mobileStepIndex = STARTUP_ONBOARDING_DISPLAY_STEPS.findIndex((s) => s.id === 'company');

  // Read from profiles.onboarding_progress — the established staging spot for
  // onboarding-scoped state that predates any `team_members` row (a fresh
  // founder has no membership yet at this step, so it can't live on
  // `team_members.founder_status`). Task 7 (personal step) writes TWO keys here:
  //  - `founder_status`        — legacy binary ('founder_or_cofounder'|'member')
  //                              that the onboarding-claim edge function reads.
  //  - `founder_status_detail` — the CANONICAL FounderStatus enum. Read THIS one;
  //                              reading `founder_status` would feed a binary
  //                              string into canCreateOrClaim() and always fail.
  const founderStatus = (authProfile?.onboarding_progress as { founder_status_detail?: FounderStatus } | null)
    ?.founder_status_detail;
  const isFounder = !!founderStatus && canCreateOrClaim(founderStatus);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ startupId: string; name: string } | null>(null);
  // Duplicate-create guard (§6): creating a page whose exact name already
  // exists AND is claimed needs an explicit warn + confirm, never a silent
  // duplicate. Holds the pending name until the user confirms or cancels.
  const [duplicateWarn, setDuplicateWarn] = useState<{ name: string } | null>(null);
  // Phase 116.2 reconciliation — a claim on a company_directory-linked match
  // routes through the existing email-code/LinkedIn verification panel
  // before the ownership bridge (claimCompany) ever runs. Set only when a
  // linked directory row is found; see handleClaim.
  const [verifyingClaim, setVerifyingClaim] = useState<{
    directoryId: string;
    startupId: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching: isSearching } = useQuery({
    queryKey: ['onboarding', 'company-search', debouncedQuery],
    queryFn: () => searchCompanies(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
  });

  const hasSearched = debouncedQuery.length >= 2;
  // An exact UNCLAIMED match steers the user to Claim (hide the create prompt);
  // an exact CLAIMED match still allows a deliberate duplicate-create, but only
  // behind a warn + confirm (see handleCreate / duplicateWarn).
  const exactUnclaimedMatch = useMemo(
    () =>
      results.some(
        (r) => !r.is_claimed && r.name.trim().toLowerCase() === debouncedQuery.toLowerCase()
      ),
    [results, debouncedQuery]
  );

  // Company step is done (create or claim) — persist
  // the resume pointer to the canonical next step (essentials) and navigate
  // there via the single source of truth for step→route (resolve-route.ts,
  // Task 5), rather than hardcoding the data-source screen the old flow
  // jumped straight to.
  const advanceToNextStep = () => {
    const step = nextStep('company', false);
    if (step) {
      if (authUser?.id) void persistOnboardingStep(authUser.id, step);
      router.push(routeForStep('startup', step));
    } else {
      // Unreachable given the canonical step list, but never strand the user.
      router.push('/(onboarding)/data-input-choice');
    }
  };

  const proceedWithStartup = (row: StartupProfileRow) => {
    setStartupProfile(rowToStoreProfile(row));
    advanceToNextStep();
  };

  // ------------------------------------------------------------------
  // Claim — founder/co-founder on an existing unclaimed page. A match
  // backed by a company_directory entry (promoted/seeded from a real
  // source — Startupticker, Zefix, a scored submission) requires proof of
  // ownership first: hand off to the existing email-code/LinkedIn
  // verification panel (Phase 116.2). A match with no directory link has
  // no external identity to verify against — claim proceeds straight to
  // the ownership bridge.
  // ------------------------------------------------------------------
  const handleClaim = async (match: CompanyMatch) => {
    if (claimingId || creatingKey) return;
    setClaimingId(match.id);
    try {
      if (!isSupabaseConfigured()) throw new Error('You must be signed in to claim a company profile.');

      const { data: directoryRow } = await supabase
        .from('company_directory')
        .select('id')
        .eq('startup_profile_id', match.id)
        .maybeSingle();

      if (directoryRow?.id) {
        setVerifyingClaim({ directoryId: directoryRow.id, startupId: match.id, name: match.name });
        return;
      }

      const { ok, code } = await bridgeClaim(match.id);
      if (ok) {
        // Activation §8 — company resolved by CLAIM, after the awaited bridge.
        track('company_resolved', { method: 'claim', companyName: match.name });
        const row = await fetchFullRow(match.id, match.name);
        proceedWithStartup(row);
      } else if (code === 'ALREADY_OWNED') {
        setConflict({ startupId: match.id, name: match.name });
      } else if (isClaimRefusalCode(code)) {
        // 119-03: a closed company (or its last founder's erasure still in
        // flight) refuses every claim with 409; say so instead of "try again".
        showError(
          code === 'company_closed' ? 'This company is closed' : 'Not available right now',
          CLAIM_REFUSAL_MESSAGES[code],
        );
      } else if (code === 'NOT_CLAIMABLE') {
        // 118-10: only the page you just created can be claimed without
        // verification. Retrying will not change that, so do not suggest it.
        showError(
          'This page can’t be claimed directly',
          'Ask its team for an invite, or create your company’s page.',
        );
      } else {
        showError('Could not claim company', 'Please try again.');
      }
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'StartupCompanyScreen.handleClaim' });
      if (!isCancelled) showError('Could not claim company', message);
    } finally {
      setClaimingId(null);
    }
  };

  // ------------------------------------------------------------------
  // Verification completed. ClaimVerificationPanel's useClaimCompany mutation
  // already made the claim through onboarding-claim { directoryId } — owner
  // membership, directory link, claimed marker — and resolved only on
  // success (a failure is surfaced in the panel and never reaches here). So
  // there is no second bridge call: ownership has exactly one path.
  // ------------------------------------------------------------------
  const handleVerifiedClaimComplete = async (result: { profileId: string; directoryId: string }) => {
    const name = verifyingClaim?.name ?? '';
    setVerifyingClaim(null);
    // Activation §8 — a verified directory claim IS a claim resolution.
    track('company_resolved', { method: 'claim', companyName: name, verified: true });
    const row = await fetchFullRow(result.profileId, name);
    proceedWithStartup(row);
  };

  // ------------------------------------------------------------------
  // Create — no match (typed name) or a registry hit with no Inverio page
  // yet. Founders/co-founders auto-claim right after (matrix: "Create +
  // claim"); members create WITHOUT claiming (matrix: "Create — no delete
  // right").
  // ------------------------------------------------------------------
  const handleCreate = async (nameRaw: string, registryMatch?: CompanyMatch, confirmedDuplicate = false) => {
    const name = capitalizeWords(nameRaw.trim());
    if (!name || claimingId || creatingKey) return;

    // §6 duplicate-create guard: an exact-name match that's already CLAIMED
    // means someone owns that page. Warn + confirm before creating a duplicate
    // (a genuinely different company can share a name) — never silently.
    if (!confirmedDuplicate) {
      const claimedConflict = results.some(
        (r) => r.is_claimed && r.name.trim().toLowerCase() === name.toLowerCase()
      );
      if (claimedConflict) {
        setDuplicateWarn({ name });
        return;
      }
    }

    const key = registryMatch?.id ?? `typed:${name}`;
    setCreatingKey(key);
    try {
      if (!isSupabaseConfigured() || !authUser?.id) {
        throw new Error('You must be signed in to create a company profile.');
      }

      // Best-effort registry enrichment before insert (spec §8: a lookup
      // failure proceeds with manual entry, never a dead end).
      let industry = '';
      let location = '';
      let website: string | undefined;
      let foundedYear: number | undefined;
      if (registryMatch) {
        try {
          const [zefix, ticker] = await Promise.all([
            lookupSwissCompany({ name }),
            enrichFromStartupticker({ name }),
          ]);
          const tickerProfile = ticker.data?.found ? ticker.data.profile : undefined;
          const zefixCompany = zefix.data?.found ? zefix.data.company : undefined;
          industry = tickerProfile?.sector || '';
          location =
            tickerProfile?.location ||
            (zefixCompany ? [zefixCompany.city, zefixCompany.canton].filter(Boolean).join(', ') : '');
          website = tickerProfile?.website;
          foundedYear = tickerProfile?.foundedYear;
        } catch (enrichErr) {
          reportError(enrichErr, { context: 'StartupCompanyScreen.handleCreate.enrich', level: 'warning' });
        }
      }

      // Plain insert, NOT an upsert keyed on user_id: ownership lives in
      // team_members now (see claimCompany below), not startup_profiles.
      // Leaving user_id unset keeps a mere "create" from being picked up by
      // useMyStartup's legacy-owner fallback — that would silently grant a
      // non-claiming member delete rights, defeating create ≠ claim.
      // Do NOT read this row back with `.select()`. A brand-new UNCLAIMED shell
      // (user_id NULL, creator not yet a team member) satisfies the INSERT
      // policy but matches NO SELECT policy, so PostgREST's return=representation
      // read-back is denied and surfaces as a spurious 42501 *on the insert*.
      // (Regressed when the world-readable startup_profiles policy was dropped
      // in the data-governance tightening — the read-back had silently depended
      // on it.) Generate the id client-side, insert with return=minimal, and
      // read the full row back only AFTER the claim/join below makes it owner-
      // or team-visible.
      const newStartupId = Crypto.randomUUID();
      const { error: insertErr } = await supabase
        .from('startup_profiles')
        .insert({
          id: newStartupId,
          name,
          industry: industry || 'Other',
          location: location || null,
          website: website || null,
          founded_year: foundedYear || null,
          profile_source: 'manual',
        });
      if (insertErr) throw insertErr;

      // Activation §8 — company resolved by CREATE, after the awaited insert.
      // Fired ONCE here even on the founder create+claim path (the bridge below
      // does NOT re-emit), so a create is never double-counted as a claim.
      track('company_resolved', {
        method: 'create',
        companyName: name,
        founderStatus: founderStatus || 'unknown',
      });

      if (isFounder) {
        // Matrix: "Page doesn't exist" + founder → Create + claim, one flow.
        // A brand-new insert has no company_directory link, so the bridge's
        // "unlinked_create" path grants ownership outright — no separate
        // verification step needed (see onboarding-claim/index.ts).
        const { ok } = await bridgeClaim(newStartupId);
        if (!ok) {
          // Non-fatal — the profile exists; surface the claim hiccup without
          // stranding the user on this screen.
          reportError(new Error('onboarding-claim bridge failed after create'), {
            context: 'StartupCompanyScreen.handleCreate.autoClaim',
            level: 'warning',
          });
        }
      } else {
        // Matrix: "Page doesn't exist" + member → Create WITHOUT claim (no
        // delete right). But the creator still needs to be a MEMBER of the page
        // they just made, or they can't resolve into it (the interview/score
        // would show "Complete Your Profile"). Enroll them as the first ACTIVE
        // member via onboarding-join's asCreator path — server-gated to this
        // ownerless brand-new shell. A founder can still claim ownership later.
        const { data: joinData, error: joinErr } = await joinCompany({
          startupId: newStartupId,
          asCreator: true,
        });
        if (joinErr || !joinData) {
          // Non-fatal — the profile exists; log without stranding the user.
          reportError(new Error('member-create active enroll failed'), {
            context: 'StartupCompanyScreen.handleCreate.memberEnroll',
            level: 'warning',
          });
        }
      }

      // The claim (founder) / join (member) above made the row readable (owner
      // or active team member), so we can now fetch the full row for the store.
      // If that step failed (non-fatal above), the shell is still unreadable —
      // fall back to a locally-composed row so the user is never stranded.
      const { data: createdRow } = await supabase
        .from('startup_profiles')
        .select('*')
        .eq('id', newStartupId)
        .maybeSingle();
      proceedWithStartup(
        (createdRow as unknown as StartupProfileRow | null) ??
          ({
            id: newStartupId,
            name,
            industry: industry || 'Other',
            location: location || null,
            website: website || null,
            founded_year: foundedYear || null,
            profile_source: 'manual',
            user_id: null,
            is_claimed: false,
          } as unknown as StartupProfileRow),
      );
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'StartupCompanyScreen.handleCreate' });
      if (!isCancelled) showError('Could not create company profile', message);
    } finally {
      setCreatingKey(null);
    }
  };

  // Search-first (Task 3): ONE searchable autocomplete decides claim vs create
  // vs resolve — no "Create vs Claim" fork before searching. When a claim is
  // mid-verification (company_directory-linked match), swap the search UI for
  // the existing ClaimVerificationPanel (Phase 116.2) — routed into, never
  // reimplemented.
  const searchIntro = isFounder
    ? 'Search to claim an existing page, or create a new one if it doesn’t exist yet.'
    : 'Search for your company. If it already has a page, ask a team member there to invite you — or create a new one if it doesn’t exist yet.';

  const searchOrVerifyContent = verifyingClaim ? (
    <ClaimVerificationPanel
      directoryId={verifyingClaim.directoryId}
      companyName={verifyingClaim.name}
      userId={authUser?.id ?? ''}
      onCancel={() => setVerifyingClaim(null)}
      onClaimComplete={handleVerifiedClaimComplete}
    />
  ) : (
    <View className="gap-5">
      <View>
        <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-1">
          Find Your Company
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-4">
          {searchIntro}
        </Text>

        <View
          className="flex-row items-center rounded-xl px-4"
          style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Search size={20} color={colors.text.tertiary} />
          <TextInput
            style={{ color: colors.text.primary }}
            className="flex-1 py-4 pl-3 text-base"
            placeholder="Enter your company name..."
            placeholderTextColor={colors.text.tertiary}
            value={query}
            onChangeText={setQuery}
            autoCapitalize="words"
            autoFocus
            accessibilityLabel="Search for your company by name"
          />
          {isSearching && <ActivityIndicator size="small" color={colors.primary[600]} />}
        </View>
      </View>

      {hasSearched && (
        <Animated.View entering={entrance(FadeInDown.duration(300))} className="gap-3">
          {results.map((match) => {
            const action = actionFor(match, isFounder);
            const isBusy = claimingId === match.id || creatingKey === match.id;
            return (
              <CompanyRow
                key={match.id}
                match={match}
                action={action}
                isBusy={isBusy}
                colors={colors}
                onClaim={() => handleClaim(match)}
                onCreate={() => handleCreate(match.name, match)}
              />
            );
          })}

          {/* Create-new prompt — available once the user has typed enough to
              search. Hidden only when an exact UNCLAIMED match exists (steer to
              Claim); an exact CLAIMED match keeps it (duplicate-create is
              allowed behind the warn + confirm in handleCreate). */}
          {!exactUnclaimedMatch && (
            <View
              className="p-5 rounded-2xl items-center"
              style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
            >
              <View
                className="w-14 h-14 rounded-full items-center justify-center mb-3"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Building2 size={28} color={colors.primary[600]} />
              </View>
              <Text style={{ color: colors.text.primary }} className="text-base font-semibold mb-1 text-center" numberOfLines={2}>
                {results.length === 0 ? 'No existing profile found' : "None of these?"}
              </Text>
              <Text style={{ color: colors.text.secondary }} className="text-sm text-center mb-4" numberOfLines={3}>
                {`"${debouncedQuery}" doesn't have a profile yet.`}
              </Text>
              <AnimatedPressable
                onPress={() => handleCreate(debouncedQuery)}
                disabled={creatingKey === `typed:${capitalizeWords(debouncedQuery)}`}
                accessibilityRole="button"
                accessibilityLabel={isFounder ? `Create ${debouncedQuery} and claim it` : `Create ${debouncedQuery}`}
                accessibilityState={{
                  disabled: creatingKey === `typed:${capitalizeWords(debouncedQuery)}`,
                  busy: creatingKey === `typed:${capitalizeWords(debouncedQuery)}`,
                }}
                className="flex-row items-center gap-2 px-6 py-3 rounded-xl"
                style={{ minHeight: 44, backgroundColor: colors.primary[600] }}
              >
                {creatingKey === `typed:${capitalizeWords(debouncedQuery)}` ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Plus size={18} color="white" />
                )}
                <Text className="text-white font-semibold" numberOfLines={1}>
                  {isFounder ? `Create "${debouncedQuery}" & claim` : `Create "${debouncedQuery}"`}
                </Text>
              </AnimatedPressable>
            </View>
          )}
        </Animated.View>
      )}

      {!hasSearched && (
        <View className="items-center py-8">
          <Text style={{ color: colors.text.tertiary }} className="text-sm text-center">
            Start typing to search for your company
          </Text>
        </View>
      )}
    </View>
  );

  // Search-first (Task 3): one unified autocomplete for founders and
  // non-founders alike. The system decides claim vs create vs resolve from the
  // match state (see actionFor + handleClaim/handleCreate); self-service Join
  // is retired, so a claimed match shows invite guidance instead of an action.
  const mainContent = searchOrVerifyContent;

  const duplicateWarnModal = (
    <AppModal
      visible={!!duplicateWarn}
      onClose={() => setDuplicateWarn(null)}
      title="A company with this name already exists"
      footer={
        <AppModal.Actions>
          <ModalAction label="Cancel" variant="neutral" onPress={() => setDuplicateWarn(null)} />
          <ModalAction
            label="Create anyway"
            variant="primary"
            onPress={() => {
              const name = duplicateWarn?.name;
              setDuplicateWarn(null);
              if (name) void handleCreate(name, undefined, true);
            }}
          />
        </AppModal.Actions>
      }
    >
      <View className="items-center gap-3 py-2">
        <View
          className="w-14 h-14 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.amber.light }}
        >
          <AlertTriangle size={26} color={colors.amber.main} />
        </View>
        <Text style={{ color: colors.text.primary, textAlign: 'center' }} className="text-base font-semibold" numberOfLines={2}>
          “{duplicateWarn?.name}” is already claimed on Inverio
        </Text>
        <Text style={{ color: colors.text.secondary, textAlign: 'center' }} className="text-sm">
          If this is your company, ask a team member there to invite you instead of creating a
          duplicate. Only create a new page if this is a genuinely different company that happens to
          share the name.
        </Text>
      </View>
    </AppModal>
  );

  const conflictModal = (
    <AppModal
      visible={!!conflict}
      onClose={() => setConflict(null)}
      title="Already claimed"
      footer={
        <AppModal.Actions>
          <ModalAction label="Cancel" variant="neutral" onPress={() => setConflict(null)} />
        </AppModal.Actions>
      }
    >
      <View className="items-center gap-3 py-2">
        <View
          className="w-14 h-14 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.amber.light }}
        >
          <AlertTriangle size={26} color={colors.amber.main} />
        </View>
        <Text style={{ color: colors.text.primary, textAlign: 'center' }} className="text-base font-semibold" numberOfLines={2}>
          {conflict?.name} already has an active owner
        </Text>
        <Text style={{ color: colors.text.secondary, textAlign: 'center' }} className="text-sm">
          Someone already claimed this company. Ask a team member there to invite you instead. If you believe
          this is wrong, contact support to dispute the claim.
        </Text>
      </View>
    </AppModal>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="company"
        steps={STARTUP_ONBOARDING_DISPLAY_STEPS}
        title="Your Company"
        subtitle="Find or create your startup profile"
        userType="startup"
        entityName={user?.name || undefined}
      >
        {mainContent}
        {conflictModal}
        {duplicateWarnModal}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View entering={entrance(FadeInDown.delay(100).duration(500))} className="px-5 pt-4 pb-4">
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              accessibilityRole="button"
              accessibilityLabel="Go back"
              className="w-11 h-11 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-1 flex-row gap-2 mx-3">
              {STARTUP_ONBOARDING_DISPLAY_STEPS.map((step, index) => (
                <View
                  key={step.id}
                  className="h-1 flex-1 rounded-full"
                  style={{
                    backgroundColor: index <= mobileStepIndex ? colors.primary[500] : colors.border.light,
                    maxWidth: 40,
                  }}
                />
              ))}
            </View>
            <OnboardingSignOut />
          </View>
        </Animated.View>

        {/* Title */}
        <Animated.View entering={entrance(FadeInDown.delay(200).duration(500))} className="px-5 pb-6">
          <View className="flex-row items-center gap-3 mb-2">
            <Building2 size={24} color={colors.primary[600]} />
            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
              Step {mobileStepIndex + 1} of {STARTUP_ONBOARDING_DISPLAY_STEPS.length}
            </Text>
          </View>
          <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
            Your Company
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
            Find or create your startup profile
          </Text>
        </Animated.View>

        {/* Content */}
        <ScrollView className="flex-1 px-5" showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
          <Animated.View entering={entrance(FadeInUp.delay(300).duration(500))} className="pb-8">
            {mainContent}
          </Animated.View>
        </ScrollView>
      </SafeAreaView>
      {conflictModal}
      {duplicateWarnModal}
    </View>
  );
}

interface CompanyRowProps {
  match: CompanyMatch;
  /** `null` = no self-service action available (retired self-service Join —
   * final-review IMPORTANT 2); the row shows non-tappable invite guidance
   * instead of a button. */
  action: MatrixAction | null;
  isBusy: boolean;
  colors: ReturnType<typeof useSystemThemeColors>;
  onClaim: () => void;
  onCreate: () => void;
}

function CompanyRow({ match, action, isBusy, colors, onClaim, onCreate }: CompanyRowProps) {
  const isRegistry = match.source === 'registry';
  const badge = isRegistry
    ? { label: 'Swiss registry', bg: colors.surface.secondary, fg: colors.text.tertiary }
    : match.is_claimed
      ? { label: 'Claimed', bg: colors.surface.secondary, fg: colors.text.tertiary }
      : { label: 'Unclaimed', bg: colors.primary[50], fg: colors.primary[700] };

  const actionConfig: Record<MatrixAction, { label: string; icon: React.ReactNode; bg: string }> = {
    claim: { label: 'Claim', icon: <Check size={14} color="white" />, bg: colors.primary[600] },
    create: { label: 'Create', icon: <Plus size={14} color="white" />, bg: colors.primary[600] },
  };
  const cfg = action ? actionConfig[action] : null;
  const onPress = action === 'claim' ? onClaim : onCreate;

  // No self-service action on this match (already claimed, or unclaimed but
  // the caller isn't a founder/co-founder) — self-service Join is retired;
  // the only path onto the team is a real invite from someone already there.
  const guidance = match.is_claimed
    ? 'Already claimed — ask a team member to invite you.'
    : 'Ask a team member to invite you.';

  return (
    <View
      className="p-4 rounded-xl flex-row items-center"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
    >
      <View
        className="w-12 h-12 rounded-xl items-center justify-center mr-3"
        style={{ backgroundColor: colors.primary[50] }}
      >
        <Text style={{ color: colors.primary[700] }} className="text-lg font-bold">
          {match.name.charAt(0).toUpperCase()}
        </Text>
      </View>

      <View className="flex-1 mr-3">
        <Text style={{ color: colors.text.primary }} className="font-semibold" numberOfLines={1}>
          {match.name}
        </Text>
        <View className="flex-row items-center gap-1 mt-1">
          {match.is_claimed && !isRegistry && <Lock size={11} color={colors.text.tertiary} />}
          <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: badge.bg }}>
            <Text style={{ color: badge.fg }} className="text-xs font-medium" numberOfLines={1}>
              {badge.label}
            </Text>
          </View>
        </View>
      </View>

      {cfg ? (
        <AnimatedPressable
          onPress={onPress}
          disabled={isBusy}
          accessibilityRole="button"
          accessibilityLabel={`${cfg.label} ${match.name}`}
          accessibilityState={{ disabled: isBusy, busy: isBusy }}
          className="flex-row items-center gap-1 px-4 py-2.5 rounded-lg"
          style={{ minHeight: 44, backgroundColor: cfg.bg, opacity: isBusy ? 0.7 : 1 }}
        >
          {isBusy ? <ActivityIndicator size="small" color="white" /> : cfg.icon}
          <Text className="text-white font-medium text-sm" numberOfLines={1}>
            {cfg.label}
          </Text>
          {!isBusy && <ArrowRight size={12} color="white" />}
        </AnimatedPressable>
      ) : (
        <Text
          style={{ color: colors.text.tertiary, maxWidth: 108 }}
          className="text-xs text-right"
          numberOfLines={2}
        >
          {guidance}
        </Text>
      )}
    </View>
  );
}
